// Lightweight <pinch-zoom> custom element with working mobile double-tap
const PinchZoom = (function () {
  "use strict";

  const globalContext = typeof self !== "undefined" ? self : typeof window !== "undefined" ? window : globalThis;

  const minScaleAttr = "min-scale";
  const maxScaleAttr = "max-scale";
  const HARD_MIN_SCALE = 1;
  const HARD_MAX_SCALE = 8;
  const DOUBLE_TAP_DELAY = 350;

  function getDistance(a, b) {
    if (!b) return 0;
    return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
  }

  function getMidpoint(a, b) {
    if (!b) return a;
    return {
      clientX: (a.clientX + b.clientX) / 2,
      clientY: (a.clientY + b.clientY) / 2,
    };
  }

  function getAbsoluteValue(value, max) {
    if (typeof value === "number") return value;
    const trimmed = String(value || "").trim();
    if (trimmed.endsWith("%")) {
      return (max * parseFloat(trimmed)) / 100;
    }
    return parseFloat(trimmed);
  }

  class PinchZoom extends HTMLElement {
    constructor() {
      super();
      this._scale = 1;
      this._x = 0;
      this._y = 0;
      this._minScale = HARD_MIN_SCALE;
      this._maxScale = HARD_MAX_SCALE;
      this._pointers = new Map();
      this._gesture = null;
      this._lastPanPoint = null;
      this._lastTap = null; // Fixed double-tap tracking
      this._contentEl = null;

      this._onPointerDown = this._onPointerDown.bind(this);
      this._onPointerMove = this._onPointerMove.bind(this);
      this._onPointerUp = this._onPointerUp.bind(this);
      this._onWheel = this._onWheel.bind(this);
      this._onDoubleClick = this._onDoubleClick.bind(this);
    }

    static get observedAttributes() {
      return [minScaleAttr, maxScaleAttr];
    }

    attributeChangedCallback() {
      this._applyLimits();
    }

    connectedCallback() {
      this.style.display = "block";
      this.style.overflow = "hidden";
      this.style.touchAction = "none";
      this.style.userSelect = "none";
      this.style.cursor = "zoom-in";

      this._refreshStage();
      this._attachListeners();
      this._applyLimits();
      this.reset();
    }

    disconnectedCallback() {
      this._detachListeners();
    }

    get minScale() {
      return this._minScale;
    }
    get maxScale() {
      return this._maxScale;
    }
    get scale() {
      return this._scale;
    }
    get x() {
      return this._x;
    }
    get y() {
      return this._y;
    }

    reset() {
      this._setTransform({ scale: this.minScale, x: 0, y: 0, allowChangeEvent: false });
    }

    scaleTo(scale, opts = {}) {
      const { originX = 0, originY = 0, relativeTo = "content", allowChangeEvent = false } = opts;
      const relativeToEl = relativeTo === "content" ? this._contentEl : this;
      if (!relativeToEl || !this._contentEl) {
        this._setTransform({ scale, allowChangeEvent });
        return;
      }
      const rect = relativeToEl.getBoundingClientRect();
      const contentRect = this._contentEl.getBoundingClientRect();
      const computedOriginX = getAbsoluteValue(originX, rect.width);
      const computedOriginY = getAbsoluteValue(originY, rect.height);
      const originXFromContent =
        relativeTo === "content" ? computedOriginX : computedOriginX - (contentRect.left - rect.left);
      const originYFromContent =
        relativeTo === "content" ? computedOriginY : computedOriginY - (contentRect.top - rect.top);

      const scaleDiff = scale / this.scale;
      this._applyChange({
        scaleDiff,
        originX: originXFromContent,
        originY: originYFromContent,
        allowChangeEvent,
      });
    }

    _attachListeners() {
      this.addEventListener("pointerdown", this._onPointerDown, { passive: false });
      this.addEventListener("wheel", this._onWheel, { passive: false });
      this.addEventListener("dblclick", this._onDoubleClick, { passive: false });
      globalContext.addEventListener("pointermove", this._onPointerMove, { passive: false });
      globalContext.addEventListener("pointerup", this._onPointerUp, { passive: false });
      globalContext.addEventListener("pointercancel", this._onPointerUp, { passive: false });
    }

    _detachListeners() {
      this.removeEventListener("pointerdown", this._onPointerDown);
      this.removeEventListener("wheel", this._onWheel);
      this.removeEventListener("dblclick", this._onDoubleClick);
      globalContext.removeEventListener("pointermove", this._onPointerMove);
      globalContext.removeEventListener("pointerup", this._onPointerUp);
      globalContext.removeEventListener("pointercancel", this._onPointerUp);
    }

    _refreshStage() {
      this._contentEl = this.firstElementChild || null;
      if (!this._contentEl) return;
      if (this.children.length > 1) {
        console.warn("<pinch-zoom> must not have more than one child.");
      }
      this._applyLimits();
      this.reset();
    }

    _applyLimits() {
      let min = parseFloat(this.getAttribute(minScaleAttr));
      let max = parseFloat(this.getAttribute(maxScaleAttr));

      if (!Number.isFinite(min)) min = HARD_MIN_SCALE;
      min = Math.max(HARD_MIN_SCALE, min);

      if (!Number.isFinite(max)) max = 3;
      max = Math.min(Math.max(max, min), HARD_MAX_SCALE);

      this._minScale = min;
      this._maxScale = max;
    }

    _onPointerDown(event) {
      if (!this._contentEl) return;
      if (event.button && event.button !== 0) return;
      event.preventDefault();

      const pointerKey = event.pointerId ?? "mouse";
      this._pointers.set(pointerKey, {
        clientX: event.clientX,
        clientY: event.clientY,
      });
      this._lastPanPoint = { clientX: event.clientX, clientY: event.clientY };

      if (this._pointers.size === 2) {
        const pts = Array.from(this._pointers.values());
        this._gesture = {
          distance: getDistance(pts[0], pts[1]),
          midpoint: getMidpoint(pts[0], pts[1]),
        };
      }
    }

    _onPointerMove(event) {
      if (!this._contentEl || !this._pointers.size) return;
      const pointerKey = event.pointerId ?? "mouse";
      if (!this._pointers.has(pointerKey)) return;
      event.preventDefault();
      this._pointers.set(pointerKey, { clientX: event.clientX, clientY: event.clientY });

      const points = Array.from(this._pointers.values());

      if (points.length === 2) {
        const midpoint = getMidpoint(points[0], points[1]);
        const currentDistance = getDistance(points[0], points[1]);
        const previousDistance = this._gesture?.distance || currentDistance;
        const scaleDiff = previousDistance ? currentDistance / previousDistance : 1;
        const panX = midpoint.clientX - (this._gesture?.midpoint?.clientX ?? midpoint.clientX);
        const panY = midpoint.clientY - (this._gesture?.midpoint?.clientY ?? midpoint.clientY);
        const origin = this._getOriginFromPoint(midpoint.clientX, midpoint.clientY);
        this._applyChange({ originX: origin.x, originY: origin.y, scaleDiff, panX, panY, allowChangeEvent: true });
        this._gesture = { distance: currentDistance, midpoint };
      } else if (points.length === 1 && this.scale > this.minScale) {
        const point = points[0];
        if (this._lastPanPoint) {
          const panX = point.clientX - this._lastPanPoint.clientX;
          const panY = point.clientY - this._lastPanPoint.clientY;
          const nextX = this.x + panX;
          const nextY = this.y + panY;
          const { x: clampedX, y: clampedY } = this._clampPosition(this.scale, nextX, nextY);
          this._setTransform({ scale: this.scale, x: clampedX, y: clampedY, allowChangeEvent: true });
        }
        this._lastPanPoint = { clientX: point.clientX, clientY: point.clientY };
      }
    }

    _onPointerUp(event) {
      const pointerKey = event.pointerId ?? "mouse";

      // --- MOBILE / TOUCH DOUBLE TAP DETECTION ---
      const now = performance.now();
      const lastTap = this._lastTap || { time: 0, x: 0, y: 0 };
      const dx = event.clientX - lastTap.x;
      const dy = event.clientY - lastTap.y;
      const moveThreshold = 20; // px
      const deltaTime = now - lastTap.time;

      if (this._pointers.size === 1 && !this._gesture && Math.hypot(dx, dy) < moveThreshold) {
        if (deltaTime > 0 && deltaTime <= DOUBLE_TAP_DELAY) {
          const fakeEvent = { clientX: event.clientX, clientY: event.clientY, preventDefault() {} };
          this._onDoubleClick(fakeEvent);
          this._lastTap = null;
        } else {
          this._lastTap = { time: now, x: event.clientX, y: event.clientY };
        }
      }

      // --- Clean up ---
      this._pointers.delete(pointerKey);
      if (this._pointers.size < 2) {
        this._gesture = null;
        // When transitioning from two-finger pinch to one-finger pan,
        // sync _lastPanPoint to the remaining pointer so we don't get
        // a huge pan delta on the next move event.
        if (this._pointers.size === 1) {
          const remaining = Array.from(this._pointers.values())[0];
          this._lastPanPoint = { clientX: remaining.clientX, clientY: remaining.clientY };
        }
      }
      if (!this._pointers.size) this._lastPanPoint = null;
    }

    _onWheel(event) {
      if (!this._contentEl) return;
      event.preventDefault();
      const divisor = event.ctrlKey ? 100 : 500;
      const scaleDiff = 1 - event.deltaY / divisor;
      const origin = this._getOriginFromPoint(event.clientX, event.clientY);
      this._applyChange({ originX: origin.x, originY: origin.y, scaleDiff, allowChangeEvent: true });
    }

    _onDoubleClick(event) {
      if (!this._contentEl) return;
      const origin = this._getOriginFromPoint(event.clientX, event.clientY);
      const isZoomedIn = this.scale > this.minScale + 0.01;
      const targetScale = isZoomedIn ? this.minScale : Math.min(this.maxScale, this.scale * 2.5);
      const scaleDiff = targetScale / this.scale;
      this._applyChange({ originX: origin.x, originY: origin.y, scaleDiff, allowChangeEvent: true });
    }

    _getOriginFromPoint(clientX, clientY) {
      const hostRect = this.getBoundingClientRect();
      const offsetX = clientX - hostRect.left;
      const offsetY = clientY - hostRect.top;
      const contentX = (offsetX - this.x) / this.scale;
      const contentY = (offsetY - this.y) / this.scale;
      return { x: contentX, y: contentY };
    }

    _applyChange(opts = {}) {
      const { scaleDiff = 1, originX = 0, originY = 0, panX = 0, panY = 0, allowChangeEvent = false } = opts;
      const prevScale = this.scale;
      const nextScaleRaw = prevScale * scaleDiff;
      const snappedScale = this._clampScale(nextScaleRaw);
      const actualScaleDiff = snappedScale / prevScale || 1;

      let nextX = this.x + panX;
      let nextY = this.y + panY;

      if (actualScaleDiff !== 1) {
        const preScaleX = originX * prevScale + this.x;
        const preScaleY = originY * prevScale + this.y;
        const postScaleX = originX * snappedScale + nextX;
        const postScaleY = originY * snappedScale + nextY;
        nextX += preScaleX - postScaleX;
        nextY += preScaleY - postScaleY;
      }

      const { x: clampedX, y: clampedY } = this._clampPosition(snappedScale, nextX, nextY);
      this._setTransform({ scale: snappedScale, x: clampedX, y: clampedY, allowChangeEvent });
    }

    _clampScale(scale) {
      const min = this.minScale;
      const max = this.maxScale;
      return Math.min(Math.max(scale, min), max);
    }

    _clampPosition(scale, x, y) {
      if (!this._contentEl) return { x, y };
      const hostRect = this.getBoundingClientRect();
      const contentWidth = this._contentEl.clientWidth || hostRect.width || 1;
      const contentHeight = this._contentEl.clientHeight || hostRect.height || 1;
      const scaledWidth = contentWidth * scale;
      const scaledHeight = contentHeight * scale;

      const minX = Math.min(0, hostRect.width - scaledWidth);
      const maxX = 0;
      let minY = Math.min(0, hostRect.height - scaledHeight);
      let maxY = 0;

      const modal = this.closest(".product-media-modal");
      if (modal && scale > this.minScale + 0.01) {
        const extraOverflow = 96;
        minY -= extraOverflow;
        maxY += extraOverflow;
      }

      return { x: Math.min(maxX, Math.max(minX, x)), y: Math.min(maxY, Math.max(minY, y)) };
    }

    _setTransform(opts = {}) {
      const { scale = this.scale, x = this.x, y = this.y, allowChangeEvent = false } = opts;
      this._scale = scale;
      this._x = x;
      this._y = y;
      if (this._contentEl) {
        this._contentEl.style.transformOrigin = "0 0";
        this._contentEl.style.transform = `translate(${this._x}px, ${this._y}px) scale(${this._scale})`;
      }
      const modal = this.closest(".product-media-modal");
      if (modal) {
        const isZoomed = this._scale > this.minScale + 0.01;
        modal.classList.toggle("product-media-modal--zoomed", isZoomed);
      }
      if (allowChangeEvent) {
        this.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }
  }

  if (!customElements.get("pinch-zoom")) {
    customElements.define("pinch-zoom", PinchZoom);
  }
  return PinchZoom;
})();
